import React, { useState, useId } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Eye, EyeOff } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    helperText?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
    label,
    error,
    helperText,
    leftIcon,
    rightIcon,
    className = '',
    disabled,
    type,
    id: providedId,
    ...props
}) => {
    const { theme } = useTheme();
    const [showPassword, setShowPassword] = useState(false);
    const generatedId = useId();
    const inputId = providedId || generatedId;
    const errorId = `${inputId}-error`;
    const helperTextId = `${inputId}-helper`;

    const isPassword = type === 'password';
    const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="w-full">
            {label && (
                <label
                    htmlFor={inputId}
                    className={`block text-sm font-medium mb-1.5 ml-1 transition-colors ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    {label}
                </label>
            )}
            <div className="relative group">
                {leftIcon && (
                    <div className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-200 ${theme === 'dark' ? 'text-gray-500 group-focus-within:text-storgia-blurple' : 'text-gray-400 group-focus-within:text-indigo-500'
                        }`}>
                        {leftIcon}
                    </div>
                )}
                <input
                    id={inputId}
                    type={inputType}
                    className={`
            w-full rounded-xl border px-4 py-2.5 text-sm transition-all duration-200
            placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0
            disabled:opacity-50 disabled:cursor-not-allowed
            ${leftIcon ? 'pl-10' : ''}
            ${rightIcon || isPassword ? 'pr-10' : ''}
            ${error
                            ? theme === 'dark'
                                ? 'bg-red-900/10 border-red-500/50 text-red-200 focus:border-red-500 focus:ring-red-500/20'
                                : 'bg-red-50 border-red-300 text-red-900 focus:border-red-500 focus:ring-red-200'
                            : theme === 'dark'
                                ? 'bg-storgia-dark border-storgia-light-blurple/20 text-white focus:border-storgia-blurple focus:ring-storgia-blurple/20 hover:border-storgia-light-blurple/40'
                                : 'bg-white border-gray-200 text-gray-900 focus:border-indigo-500 focus:ring-indigo-100 hover:border-gray-300'
                        }
            ${className}
          `}
                    disabled={disabled}
                    aria-invalid={error ? 'true' : undefined}
                    aria-describedby={
                        error ? errorId : helperText ? helperTextId : undefined
                    }
                    {...props}
                />
                {(rightIcon || isPassword) && (
                    <div className={`absolute right-3 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 ${theme === 'dark' ? 'text-gray-400 group-focus-within:text-storgia-blurple' : 'text-gray-500 group-focus-within:text-indigo-500'
                        }`}>
                        {isPassword ? (
                            <button
                                type="button"
                                onClick={togglePasswordVisibility}
                                className="focus:outline-none hover:text-storgia-blurple transition-colors cursor-pointer p-1"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? (
                                    <EyeOff className="h-5 w-5" />
                                ) : (
                                    <Eye className="h-5 w-5" />
                                )}
                            </button>
                        ) : (
                            rightIcon
                        )}
                    </div>
                )}
            </div>
            {(error || helperText) && (
                <p
                    id={error ? errorId : helperTextId}
                    className={`mt-1.5 text-xs ml-1 animate-in slide-in-from-top-1 duration-200 ${error
                        ? theme === 'dark' ? 'text-red-400' : 'text-red-600'
                        : theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}
                    role={error ? 'alert' : undefined}
                >
                    {error || helperText}
                </p>
            )}
        </div>
    );
};
